import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    Typography,
    Alert,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import {
    getSinavListesiBySezon,
    getOgrenciListesiBySinav,
    getFinalSinavSonucPdf,
    getTopluFinalSinavPdf,
    getTekliSertifikaPdf,
    getTopluSertifikaPdf
} from '../../../services/api/sinavService';
import { SinavError } from '../../../types/ErrorModals/SinavError';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GroupIcon from '@mui/icons-material/Group';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SinavDetayRequestModal } from '../../../types/RequestModals/SinavDetayRequestModal';
import { SertifikaRequestModal } from '../../../types/RequestModals/SertifikaRequestModal';
import { TopluSertifikaRequestModal } from '../../../types/RequestModals/TopluSertifikaRequestModal';
import { SinavListesiResponseModal } from '../../../types/modals';
import { OgrenciSinavListesiResponseModal } from '../../../types/modals';
import { OgrenciSinavListesiRequestModal } from '../../../types/RequestModals/OgrenciSinavListesiRequestModal';

const availableSeasons = ['2021', '2022', '2023', '2024', '2025', '2026', '2027'];

const CEFR_SEVIYELERI = ['Pre A1', 'A1', 'A1+', 'A2', 'A2+', 'B1', 'B1+', 'B2'];

/** Dosya seçimini Base64 string'e çevirir */
const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            // data:image/png;base64,... → sadece base64 kısmını al
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

interface SertifikaDialogState {
    open: boolean;
    ogrenciId: number | null;
    ogrenciIsim: string;
}

const OgretmenSinavKarne: React.FC = () => {
    const theme = useTheme();
    const [selectedSeason, setSelectedSeason] = useState<string>('2025');
    const [selectedSinavId, setSelectedSinavId] = useState<string>('');
    const [okulKoduFilter, setOkulKoduFilter] = useState<string>('');
    const [sinifiFilter, setSinifiFilter] = useState<string>('');
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [isBulkDownloading, setIsBulkDownloading] = useState(false);
    const [isBulkSertifikaDownloading, setIsBulkSertifikaDownloading] = useState(false);

    // Sertifika dialog state
    const [sertifikaDialog, setSertifikaDialog] = useState<SertifikaDialogState>({
        open: false,
        ogrenciId: null,
        ogrenciIsim: ''
    });
    const [cefrSeviyesi, setCefrSeviyesi] = useState<string>('A1');
    const [issueDate, setIssueDate] = useState<string>('');
    const [okulLogoFile, setOkulLogoFile] = useState<File | null>(null);
    const [kurumLogoFile, setKurumLogoFile] = useState<File | null>(null);
    const [sertifikaDownloading, setSertifikaDownloading] = useState(false);
    // Sertifika Bilgileri ayar dialogu
    const [sertifikaBilgileriOpen, setSertifikaBilgileriOpen] = useState(false);

    const kurumKodu = useMemo(() => localStorage.getItem('kurumKodu') || '', []);

    // 1. Query: Sezon değiştiğinde sınav listesini çek
    const {
        data: sinavListesi,
        isLoading: isSinavListesiLoading,
        error: sinavListesiError
    } = useQuery({
        queryKey: ['sinavListesi', kurumKodu, selectedSeason],
        queryFn: () => getSinavListesiBySezon({
            kurumKodu: kurumKodu,
            sezon: parseInt(selectedSeason) + 1
        }),
        enabled: !!kurumKodu,
        retry: 1
    });

    // 2. Query: Filtrelenmiş öğrenci listesini çek
    const {
        data: ogrenciListesi,
        isLoading: isOgrenciListesiLoading,
        error: ogrenciListesiError,
        refetch: refetchOgrenciListesi
    } = useQuery({
        queryKey: ['ogrenciListesi', kurumKodu, selectedSeason, selectedSinavId, okulKoduFilter, sinifiFilter],
        queryFn: async () => {
            return getOgrenciListesiBySinav({
                kurumKodu: kurumKodu,
                sezon: parseInt(selectedSeason) + 1,
                sinavId: parseInt(selectedSinavId),
                okulKodu: okulKoduFilter || undefined,
                sinifi: sinifiFilter || undefined
            });
        },
        enabled: false, // Sadece 'Filtrele' butonuna basınca çalışsın
        retry: 1
    });

    // 3. Mutation: PDF indirme işlemi
    const pdfDownloadMutation = useMutation({
        mutationFn: async (ogrenciId: number) => {
            setDownloadingId(ogrenciId);
            const requestData: SinavDetayRequestModal = {
                kurumKodu: kurumKodu,
                sinavId: parseInt(selectedSinavId),
                ogrenciId: ogrenciId,
                sezon: parseInt(selectedSeason) + 1
            };
            return getFinalSinavSonucPdf(requestData);
        },
        onSuccess: (blob, ogrenciId) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `final_sinav_sonuc_${selectedSinavId}_${ogrenciId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
        onError: (error) => {
            console.error('PDF indirme hatası:', error);
            // Hata mesajı göster (örn: toast notification)
        },
        onSettled: () => {
            setDownloadingId(null);
        }
    });

    const bulkPdfDownloadMutation = useMutation({
        mutationFn: async () => {
            const requestData: OgrenciSinavListesiRequestModal = {
                kurumKodu: kurumKodu,
                sezon: parseInt(selectedSeason) + 1,
                sinavId: parseInt(selectedSinavId),
                okulKodu: okulKoduFilter || undefined,
                sinifi: sinifiFilter || undefined
            };
            return getTopluFinalSinavPdf(requestData);
        },
        onSuccess: (blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `toplu_karne_${selectedSinavId}_${selectedSeason}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            // Başarı bildirimi (toast) gösterebilirsiniz
        },
        onError: (error) => {
            console.error('Toplu PDF indirme hatası:', error);
            // Hata bildirimi (toast) gösterebilirsiniz
        },
        onSettled: () => {
            setIsBulkDownloading(false);
        }
    });

    const bulkSertifikaDownloadMutation = useMutation({
        mutationFn: async () => {
            let okulLogoBase64: string | undefined;
            let kurumLogoBase64: string | undefined;
            if (okulLogoFile) okulLogoBase64 = await fileToBase64(okulLogoFile);
            if (kurumLogoFile) kurumLogoBase64 = await fileToBase64(kurumLogoFile);

            const requestData: TopluSertifikaRequestModal = {
                kurumKodu: kurumKodu,
                sezon: parseInt(selectedSeason) + 1,
                sinavId: parseInt(selectedSinavId),
                okulKodu: okulKoduFilter || undefined,
                sinifi: sinifiFilter || undefined,
                cefrSeviyesi: cefrSeviyesi,
                issueDate: issueDate,
                okulLogoBase64: okulLogoBase64,
                kurumLogoBase64: kurumLogoBase64
            };
            return getTopluSertifikaPdf(requestData);
        },
        onSuccess: (blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `toplu_sertifika_${selectedSinavId}_${cefrSeviyesi}_${selectedSeason}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
        onError: (error) => {
            console.error('Toplu sertifika indirme hatası:', error);
        },
        onSettled: () => {
            setIsBulkSertifikaDownloading(false);
        }
    });

    const handleBulkSertifikaDownload = () => {
        if (!selectedSinavId || !ogrenciListesi?.length || !cefrSeviyesi || !issueDate) return;
        setIsBulkSertifikaDownloading(true);
        bulkSertifikaDownloadMutation.mutate();
    };

    const handleBulkDownload = () => {
        if (!selectedSinavId || !ogrenciListesi?.length) {
            // Buton normalde disabled olmalı, ama ekstra kontrol
            return;
        }
        setIsBulkDownloading(true);
        bulkPdfDownloadMutation.mutate();
    };

    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedSinavId) {
            refetchOgrenciListesi();
        }
    };

    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
        setSelectedSinavId(''); // Sezon değişince sınav seçimini sıfırla
    };

    // ─── Sertifika Dialog İşlemleri ───
    const handleOpenSertifikaDialog = useCallback((ogrenciId: number, ogrenciIsim: string) => {
        setSertifikaDialog({ open: true, ogrenciId, ogrenciIsim });
        // Parametreler kalıcı — sıfırlanmaz
    }, []);

    const handleCloseSertifikaDialog = useCallback(() => {
        setSertifikaDialog({ open: false, ogrenciId: null, ogrenciIsim: '' });
    }, []);

    const handleSertifikaDownload = useCallback(async () => {
        if (!sertifikaDialog.ogrenciId || !cefrSeviyesi || !issueDate) return;

        setSertifikaDownloading(true);
        try {
            let okulLogoBase64: string | undefined;
            let kurumLogoBase64: string | undefined;

            if (okulLogoFile) {
                okulLogoBase64 = await fileToBase64(okulLogoFile);
            }
            if (kurumLogoFile) {
                kurumLogoBase64 = await fileToBase64(kurumLogoFile);
            }

            const requestData: SertifikaRequestModal = {
                kurumKodu: kurumKodu,
                sezon: parseInt(selectedSeason) + 1,
                sinavId: parseInt(selectedSinavId),
                ogrenciId: sertifikaDialog.ogrenciId,
                cefrSeviyesi: cefrSeviyesi,
                issueDate: issueDate,
                okulLogoBase64: okulLogoBase64,
                kurumLogoBase64: kurumLogoBase64
            };

            const blob = await getTekliSertifikaPdf(requestData);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `sertifika_${selectedSinavId}_${sertifikaDialog.ogrenciId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            handleCloseSertifikaDialog();
        } catch (error) {
            console.error('Sertifika indirme hatası:', error);
        } finally {
            setSertifikaDownloading(false);
        }
    }, [sertifikaDialog.ogrenciId, cefrSeviyesi, issueDate, okulLogoFile, kurumLogoFile, kurumKodu, selectedSeason, selectedSinavId, handleCloseSertifikaDialog]);

    const renderOgrenciListesiSkeletons = () => (
        [...Array(5)].map((_, index) => (
            <TableRow key={index}>
                <TableCell><Skeleton variant="text" /></TableCell>
                <TableCell><Skeleton variant="text" /></TableCell>
                <TableCell><Skeleton variant="text" /></TableCell>
                <TableCell><Skeleton variant="text" /></TableCell>
                <TableCell align="center"><Skeleton variant="circular" width={32} height={32} /></TableCell>
                <TableCell align="center"><Skeleton variant="circular" width={32} height={32} /></TableCell>
            </TableRow>
        ))
    );

    return (
        <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
            <Typography
                variant="h5"
                component="h1"
                sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}
            >
                <GroupIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.75rem' }} />
                Toplu Sınav Raporu
            </Typography>

            {/* Filtreleme Formu */}
            <Paper
                component="form"
                onSubmit={handleFilterSubmit}
                elevation={2}
                sx={{ p: 3, mb: 3, borderRadius: theme.shape.borderRadius * 1.5 }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                            <Select
                                labelId="season-select-label"
                                value={selectedSeason}
                                onChange={handleSeasonChange}
                                label="Eğitim Sezonu"
                            >
                                {availableSeasons.map((season) => (
                                    <MenuItem key={season} value={season}>
                                        {season} - {(parseInt(season) + 1)} Eğitim Yılı
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth variant="outlined" size="small" disabled={isSinavListesiLoading}>
                            <InputLabel id="sinav-select-label">Sınav Seçiniz</InputLabel>
                            <Select
                                labelId="sinav-select-label"
                                value={selectedSinavId}
                                onChange={(e) => setSelectedSinavId(e.target.value)}
                                label="Sınav Seçiniz"
                                required
                            >
                                {isSinavListesiLoading ? (
                                    <MenuItem value=""><em>Yükleniyor...</em></MenuItem>
                                ) : (
                                    sinavListesi?.map((sinav: SinavListesiResponseModal) => (
                                        <MenuItem key={sinav.sinavId} value={sinav.sinavId.toString()}>
                                            {format(new Date(sinav.tarih), 'dd/MM/yy', { locale: tr })} - {sinav.sinavAdi}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {sinavListesiError && (
                                <Alert severity="error" sx={{ mt: 1 }}>Sınav listesi yüklenemedi.</Alert>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Okul Kodu (Opsiyonel)"
                            value={okulKoduFilter}
                            onChange={(e) => setOkulKoduFilter(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Sınıf (Opsiyonel)"
                            value={sinifiFilter}
                            onChange={(e) => setSinifiFilter(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<FilterListIcon />}
                            disabled={!selectedSinavId || isOgrenciListesiLoading}
                        >
                            Filtrele
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
             
            {/* Sertifika Bilgileri butonu + toplu indirme */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, my: 2 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<WorkspacePremiumIcon />}
                    onClick={() => setSertifikaBilgileriOpen(true)}
                >
                    Sertifika Bilgileri
                </Button>
                {ogrenciListesi && ogrenciListesi.length > 0 && (
                    <>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={isBulkDownloading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                            disabled={isBulkDownloading}
                            onClick={handleBulkDownload}
                        >
                            {isBulkDownloading ? 'Oluşturuluyor...' : `Listeyi İndir (${ogrenciListesi.length} Öğrenci)`}
                        </Button>
                        <Tooltip title={!cefrSeviyesi || !issueDate ? 'Önce Sertifika Bilgileri belirleyin' : ''}>
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={isBulkSertifikaDownloading ? <CircularProgress size={20} color="inherit" /> : <WorkspacePremiumIcon />}
                                    disabled={isBulkSertifikaDownloading || !cefrSeviyesi || !issueDate}
                                    onClick={handleBulkSertifikaDownload}
                                >
                                    {isBulkSertifikaDownloading ? 'Oluşturuluyor...' : `Toplu Sertifika İndir (${ogrenciListesi.length} Öğrenci)`}
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            </Box>

            {/* Öğrenci Listesi */}
            <TableContainer
                component={Paper}
                elevation={2}
                sx={{ borderRadius: theme.shape.borderRadius * 1.5 }}
            >
                <Table aria-label="öğrenci sınav listesi">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Öğrenci No</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Adı Soyadı</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Sınıfı</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Okul Kodu</TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Karne</TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Sertifika</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isOgrenciListesiLoading ? (
                            renderOgrenciListesiSkeletons()
                        ) : ogrenciListesiError ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Alert severity="error" variant="filled">
                                        {(ogrenciListesiError as Error).message}
                                    </Alert>
                                </TableCell>
                            </TableRow>
                        ) : !ogrenciListesi?.length ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                                        Lütfen filtreleme yapmak için bir sınav seçip 'Filtrele' butonuna basınız veya seçtiğiniz filtreye uygun öğrenci bulunamadı.
                                    </Alert>
                                </TableCell>
                            </TableRow>
                        ) : (
                            ogrenciListesi.map((ogrenci: OgrenciSinavListesiResponseModal) => (
                                <TableRow
                                    key={ogrenci.ogrenciId}
                                    hover
                                    sx={{ "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover } }}
                                >
                                    <TableCell>{ogrenci.ogrenciNo}</TableCell>
                                    <TableCell>{ogrenci.isim}</TableCell>
                                    <TableCell>{ogrenci.sinif}</TableCell>
                                    <TableCell>{ogrenci.okulKodu}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Karne PDF'ini İndir">
                                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                <IconButton
                                                    color="secondary"
                                                    onClick={() => pdfDownloadMutation.mutate(ogrenci.ogrenciId)}
                                                    disabled={downloadingId === ogrenci.ogrenciId}
                                                >
                                                    <DownloadIcon />
                                                </IconButton>
                                                {downloadingId === ogrenci.ogrenciId && (
                                                    <CircularProgress
                                                        size={32} // IconButton boyutuyla eşleşmesi için
                                                        color="secondary"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            marginTop: '-16px',
                                                            marginLeft: '-16px',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Sertifika İndir">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleOpenSertifikaDialog(ogrenci.ogrenciId, ogrenci.isim)}
                                            >
                                                <WorkspacePremiumIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Sertifika İndirme Dialogu (öğrenci bazlı — parametreler kalıcı) */}
            <Dialog
                open={sertifikaDialog.open}
                onClose={handleCloseSertifikaDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: theme.shape.borderRadius * 2 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                    <WorkspacePremiumIcon color="primary" />
                    Sertifika İndir
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {/* Öğrenci Bilgisi */}
                        <Alert severity="info" variant="outlined" icon={false}>
                            <Typography variant="body2" color="text.secondary">Öğrenci</Typography>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {sertifikaDialog.ogrenciIsim}
                            </Typography>
                        </Alert>

                        {/* Mevcut sertifika parametrelerini göster */}
                        <Alert
                            severity={cefrSeviyesi && issueDate ? 'success' : 'warning'}
                            variant="outlined"
                            icon={false}
                        >
                            {cefrSeviyesi && issueDate ? (
                                <>
                                    <Typography variant="body2" color="text.secondary">CEFR Seviyesi</Typography>
                                    <Typography variant="body1" fontWeight={600}>{cefrSeviyesi}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Sertifika Tarihi</Typography>
                                    <Typography variant="body1" fontWeight={600}>{issueDate}</Typography>
                                    {okulLogoFile && (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>🏫 Okul Logosu: {okulLogoFile.name}</Typography>
                                    )}
                                    {kurumLogoFile && (
                                        <Typography variant="body2">🏛️ Kurum Logosu: {kurumLogoFile.name}</Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body2">
                                    Lütfen önce <strong>Sertifika Bilgileri</strong> butonuna tıklayarak CEFR seviyesi ve tarihi belirleyin.
                                </Typography>
                            )}
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={handleCloseSertifikaDialog}
                        disabled={sertifikaDownloading}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSertifikaDownload}
                        disabled={!cefrSeviyesi || !issueDate || sertifikaDownloading}
                        startIcon={sertifikaDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    >
                        {sertifikaDownloading ? 'Oluşturuluyor...' : 'Sertifika İndir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sertifika Bilgileri Ayar Dialogu */}
            <Dialog
                open={sertifikaBilgileriOpen}
                onClose={() => setSertifikaBilgileriOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: theme.shape.borderRadius * 2 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                    <WorkspacePremiumIcon color="primary" />
                    Sertifika Bilgileri
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ pt: 1 }}>
                        {/* CEFR Seviyesi */}
                        <FormControl fullWidth size="small" required>
                            <InputLabel id="cefr-select-label">CEFR Seviyesi</InputLabel>
                            <Select
                                labelId="cefr-select-label"
                                value={cefrSeviyesi}
                                onChange={(e) => setCefrSeviyesi(e.target.value)}
                                label="CEFR Seviyesi"
                            >
                                {CEFR_SEVIYELERI.map((seviye) => (
                                    <MenuItem key={seviye} value={seviye}>
                                        {seviye}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Sertifika Tarihi */}
                        <TextField
                            fullWidth
                            size="small"
                            label="Sertifika Tarihi"
                            placeholder="Örn: June 2026"
                            value={issueDate}
                            onChange={(e) => setIssueDate(e.target.value)}
                            required
                            helperText="Sertifika üzerinde gösterilecek tarih (örn: June 2026)"
                        />

                        {/* Okul Logosu */}
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Okul Logosu (Opsiyonel)
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                                color={okulLogoFile ? 'success' : 'primary'}
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                            >
                                {okulLogoFile ? okulLogoFile.name : 'Logo Seçiniz (PNG/JPG)'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={(e) => setOkulLogoFile(e.target.files?.[0] || null)}
                                />
                            </Button>
                        </Box>

                        {/* Kurum Logosu */}
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Kurum Logosu (Opsiyonel)
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                                color={kurumLogoFile ? 'success' : 'primary'}
                                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                            >
                                {kurumLogoFile ? kurumLogoFile.name : 'Logo Seçiniz (PNG/JPG)'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={(e) => setKurumLogoFile(e.target.files?.[0] || null)}
                                />
                            </Button>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button variant="contained" onClick={() => setSertifikaBilgileriOpen(false)}>
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OgretmenSinavKarne;